/**
 * Username + photo for one teammate — how the team recognises and calls
 * each other in Aster Phone. Used for "My profile" and by managers on the
 * Staff page. The server checks who may change whose profile.
 */
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ts } from './api';
import Avatar from './Avatar';
import { Field } from './ui';

export default function ProfileEditor({ member, onSaved }) {
  const [handle, setHandle] = useState(member.handle || '');
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await onSaved?.(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('That photo is too big (8 MB max)'); return; }
    run(() => ts.uploadPhoto(member.id, f), 'Photo updated');
  };
  return (
    <div className="flex flex-wrap items-end gap-4" data-testid={`ts-profile-${member.id}`}>
      <div className="flex items-center gap-3">
        <Avatar id={member.id} name={member.full_name} version={member.avatar_version} size={56} />
        <div className="flex flex-col gap-1">
          <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} data-testid="ts-photo-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => file.current?.click()}>
            {member.avatar_version ? 'Change photo' : 'Upload photo'}
          </Button>
          {member.avatar_version && (
            <button type="button" className="text-xs text-muted-foreground hover:text-red-500 text-left" disabled={busy}
              onClick={() => run(() => ts.removePhoto(member.id), 'Photo removed')}>Remove photo</button>
          )}
        </div>
      </div>
      <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); run(async () => { const r = await ts.setUsername(member.id, handle.trim()); setHandle(r.handle || ''); }, handle.trim() ? 'Username saved' : 'Username removed'); }}>
        <Field label="Username" hint="Shown above the business number in Aster Phone. Leave empty to show just the number.">
          <Input value={handle} onChange={e => setHandle(e.target.value)} placeholder="e.g. abc_support" maxLength={33} className="w-56" data-testid="ts-username" />
        </Field>
        <Button size="sm" type="submit" disabled={busy} data-testid="ts-username-save">Save</Button>
      </form>
    </div>
  );
}
